import FormatCurrency from 'components/Common/FormatCurrency'
import List from 'components/Common/List'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import ModalContext from 'contexts/ModalContext'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext } from 'react'
import * as React from 'react'
import * as OFFER_TYPES from 'constants/offer'
import { FormattedMessage, useIntl } from 'react-intl'

interface Props {
  commissionPercentage: number
  productType: App.ProductType
  offerTotal?: number
  multipleCommissions?: boolean
  greatestCommission: number
  minBaseCommission?: number
  maxBaseCommission?: number
  includesTaxesAndFees?: boolean
}

function PriceRowCommission(props: Props) {
  const {
    commissionPercentage,
    productType,
    offerTotal = 0,
    multipleCommissions = false,
    greatestCommission,
    minBaseCommission,
    maxBaseCommission,
    includesTaxesAndFees = true,
  } = props

  const intl = useIntl()
  const hasTotal = offerTotal > 0

  const hotelLabel = intl.formatMessage({
    id: 'BOglDXqFdQpJjMQXwqKn',
    defaultMessage: 'hotel',
    description: 'Product type display name for hotel offers in commission details',
  })
  const tourLabel = intl.formatMessage({
    id: 'RWtku4g6HeUD9NeCWfvQ',
    defaultMessage: 'tour',
    description: 'Product type display name for tour offers in commission details',
  })
  const cruiseLabel = intl.formatMessage({
    id: 'vEMeqeHSJbMLvQsbVIzC',
    defaultMessage: 'cruise',
    description: 'Product type display name for cruise offers in commission details',
  })
  const carHireLabel = intl.formatMessage({
    id: 'H_vxhF9Wy1P1hiLsmjkY',
    defaultMessage: 'car hire',
    description: 'Product type display name for car hire offers in commission details',
  })

  const displayProductTypeMap: Partial<Record<App.ProductType, string>> = {
    [OFFER_TYPES.OFFER_TYPE_BED_BANK]: hotelLabel,
    [OFFER_TYPES.OFFER_TYPE_HOTEL]: hotelLabel,
    [OFFER_TYPES.OFFER_TYPE_ALWAYS_ON]: hotelLabel,
    [OFFER_TYPES.PRODUCT_TYPE_TOUR_ULTRA_LUX]: tourLabel,
    [OFFER_TYPES.PRODUCT_TYPE_TOUR_PREMIUM]: tourLabel,
    [OFFER_TYPES.PRODUCT_TYPE_TOUR_DELUXE]: tourLabel,
    [OFFER_TYPES.PRODUCT_TYPE_TOUR_V2]: tourLabel,
    [OFFER_TYPES.PRODUCT_TYPE_CRUISE_FLASH]: cruiseLabel,
    [OFFER_TYPES.PRODUCT_TYPE_CAR_HIRE]: carHireLabel,
  }

  const displayProductTypeName = displayProductTypeMap[productType] || productType
  const upToCommissionPercentage = Math.max(maxBaseCommission || 0, greatestCommission)

  const modalTitle = intl.formatMessage({
    id: '9TyZ8tHJkvUIuRNiCcer',
    defaultMessage: 'Commission details',
    description: 'Title for the commission details modal in agent hub',
  })

  const earnEstimateText = intl.formatMessage(
    {
      id: 'mhmMCYdzjuZEly9MZENq',
      defaultMessage: 'Earn up to {percentage}% on this deal',
      description: 'Commission estimate text shown when exact commission is variable',
    },
    { percentage: upToCommissionPercentage },
  )

  const earnExactText = intl.formatMessage(
    {
      id: 'A_XGRPDSNaWYD5DLLItx',
      defaultMessage: 'Earn a {percentage}% commission on this deal',
      description: 'Commission text shown when exact commission percentage is known',
    },
    { percentage: commissionPercentage },
  )

  const howItWorksLabel = intl.formatMessage({
    id: 'tMceyQaJUwPR_nEbnyyh',
    defaultMessage: 'How it works:',
    description: 'Heading for the commission explanation list in details modal',
  })

  const commissionCalculationText = intl.formatMessage(
    {
      id: '72jhOkWJO3btt3D3sHt9',
      defaultMessage: 'Your commission is calculated from the total booking amount for this {productType}.',
      description: 'Explains how agent commission is calculated for a given product type',
    },
    { productType: displayProductTypeName },
  )

  const variableCommissionText = intl.formatMessage({
    id: 'hVgZtUdBSc7xFn_s6kpg',
    defaultMessage: 'Commission is variable for this offer and will only be calculated once dates have been selected and rates returned by our suppliers',
    description: 'Explains that bed bank offer commission varies based on selected dates and supplier rates',
  })

  const commissionRangeText = intl.formatMessage(
    {
      id: '2tQZVJF1R3POQCBky4HG',
      defaultMessage: 'Once the rates are returned, we will calculate the commission which will range from {minPercentage}% to {maxPercentage}%',
      description: 'Explains the commission percentage range for bed bank offers',
    },
    { minPercentage: minBaseCommission, maxPercentage: maxBaseCommission },
  )

  const netPaymentText = intl.formatMessage({
    id: 'VMui_d4TY2MueoIiKXTE',
    defaultMessage: 'Make the net payment, which accounts for the deduction of the commission from your total booking cost.',
    description: 'Explains that agent pays net amount after commission deduction',
  })

  const monitorCommissionsText = intl.formatMessage({
    id: '0wzU3KfRprSU3op4iick',
    defaultMessage: 'Easily monitor your commissions in real-time through your agent dashboard.',
    description: 'Informs agents they can track commissions via their dashboard',
  })

  const bedBankCommissionLabel = intl.formatMessage(
    {
      id: 's19G31xWm6S-9prgdKRX',
      defaultMessage: 'Earn up to {percentage}%+ commission',
      description: 'Commission link text for bed bank offers showing maximum commission percentage',
    },
    { percentage: upToCommissionPercentage },
  )

  const showModal = useContext(ModalContext)
  const openModal = useCallback<MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    showModal(<Modal title={modalTitle}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large">
          {multipleCommissions ? earnEstimateText : earnExactText}
        </BodyText>
        <BodyText variant="large" weight="bold">
          {howItWorksLabel}
        </BodyText>
        <List>
          <BodyText variant="large" as="li">
            {commissionCalculationText}
          </BodyText>
          {productType === OFFER_TYPES.OFFER_TYPE_BED_BANK && <>
            <BodyText variant="large" as="li">
              {variableCommissionText}
            </BodyText>
            <BodyText variant="large" as="li">
              {commissionRangeText}
            </BodyText>
          </>}
          <BodyText variant="large" as="li">
            {netPaymentText}
          </BodyText>
          <BodyText variant="large" as="li">
            {monitorCommissionsText}
          </BodyText>
        </List>
      </VerticalSpacer>
    </Modal>)
  }, [showModal, modalTitle, multipleCommissions, earnEstimateText, earnExactText, howItWorksLabel, commissionCalculationText, productType, variableCommissionText, commissionRangeText, netPaymentText, monitorCommissionsText])

  const excludesTaxesAndFeesSuffix = !includesTaxesAndFees ? (
    <FormattedMessage
      id="vGo6W_FG3RCT94wlP4Zl"
      defaultMessage=" (excl. taxes and fees)"
      description="Suffix when agent commission is quoted excluding taxes and fees"
    />
  ) : null

  if (productType === OFFER_TYPES.OFFER_TYPE_BED_BANK && commissionPercentage > 0 && multipleCommissions) {
    return <Caption variant="medium">
      <TextLink
        colour="highlight-secondary"
        endIcon={<LineInfoCircleIcon colour="neutral-one" />}
        onClick={openModal}
      >
        {bedBankCommissionLabel}
        {excludesTaxesAndFeesSuffix}
      </TextLink>
    </Caption>
  }

  let commissionContent: React.ReactNode
  if (hasTotal && multipleCommissions) {
    commissionContent = <FormattedMessage
      id="yQruNTFdW9EOa_7IwVho"
      defaultMessage="Earn up to {amount} commission ({percentage}%)"
      description="Commission link text showing estimated commission amount and percentage for offers with multiple commission rates"
      values={{
        amount: <FormatCurrency value={offerTotal * (greatestCommission / 100)} />,
        percentage: greatestCommission,
      }}
    />
  } else if (hasTotal) {
    commissionContent = <FormattedMessage
      id="x7_kL_wnNienRNWfHUss"
      defaultMessage="Earn {amount} commission ({percentage}%)"
      description="Commission link text showing exact commission amount and percentage"
      values={{
        amount: <FormatCurrency value={offerTotal * (greatestCommission / 100)} />,
        percentage: greatestCommission,
      }}
    />
  } else if (multipleCommissions) {
    commissionContent = intl.formatMessage(
      {
        id: 'LaVGAQw04SPDtQdSGJaI',
        defaultMessage: 'Earn up to {percentage}% commission',
        description: 'Commission link text showing estimated commission percentage for offers with multiple rates',
      },
      { percentage: greatestCommission },
    )
  } else {
    commissionContent = intl.formatMessage(
      {
        id: 'aWApPw5ERyYChUhiw0eb',
        defaultMessage: 'Earn {percentage}% commission',
        description: 'Commission link text showing exact commission percentage for agent offers',
      },
      { percentage: greatestCommission },
    )
  }

  return <Caption variant="medium">
    <TextLink
      colour="highlight-secondary"
      endIcon={<LineInfoCircleIcon colour="neutral-one" />}
      onClick={openModal}
    >
      {commissionContent}
      {excludesTaxesAndFeesSuffix}
    </TextLink>
  </Caption>
}

export default PriceRowCommission
